import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, MailX } from "lucide-react";
import axios from "axios";
import ClinqLogo from "../components/ClinqLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [state, setState] = useState({ loading: true, error: "", email: "", status: "" });
  const [choice, setChoice] = useState(null); // true | false | null
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState({ loading: false, error: "This unsubscribe link is missing its token.", email: "", status: "" }); return; }
    axios.post(`${API}/email/unsubscribe`, { token })
      .then(({ data }) => {
        setState({ loading: false, error: "", email: data.email_masked, status: data.status });
        if (data.status === "unsubscribed" && data.funding_outreach_ok) setChoice(true);
      })
      .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "This unsubscribe link is invalid or has expired.", email: "", status: "" }));
  }, [token]);

  const pick = async (ok) => {
    setSaving(true);
    try {
      await axios.post(`${API}/email/unsubscribe/choice`, { token, funding_outreach_ok: ok });
      setChoice(ok);
    } catch (_) { /* keep buttons */ } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#04302E] text-white" data-testid="unsubscribe-page">
      <div className="mx-auto flex max-w-xl flex-col px-6 py-10">
        <Link to="/" className="mb-10 inline-flex"><ClinqLogo /></Link>
        {state.loading ? (
          <div className="flex items-center gap-2 text-white/70"><Loader2 className="h-4 w-4 animate-spin" /> Updating your preferences…</div>
        ) : state.error ? (
          <p className="rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" data-testid="unsubscribe-error">{state.error}</p>
        ) : (
          <>
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-400/15 ring-2 ring-emerald-400/30">
              <MailX className="h-5 w-5 text-emerald-300" />
            </div>
            <h1 className="mt-5 text-3xl font-extrabold tracking-tight sm:text-4xl" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="unsubscribe-ack">
              You&rsquo;ve been unsubscribed.
            </h1>
            <p className="mt-3 text-base text-white/70">
              <b className="text-white">{state.email}</b> will no longer receive marketing or lifecycle emails from Clinq.
            </p>

            {choice === null ? (
              <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="unsubscribe-question">
                <p className="text-lg font-bold leading-snug">
                  Before you go — if we come across funders who are interested in businesses in your industry, should we still reach out?
                </p>
                <div className="mt-5 grid gap-3">
                  <button data-testid="unsubscribe-yes" disabled={saving} onClick={() => pick(true)}
                    className="rounded-md bg-[#0A8B85] px-5 py-3 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50">
                    Yes, reach out for funding opportunities
                  </button>
                  <button data-testid="unsubscribe-no" disabled={saving} onClick={() => pick(false)}
                    className="rounded-md border border-white/15 bg-white/[0.04] px-5 py-3 text-sm font-semibold text-white/80 hover:bg-white/[0.08] disabled:opacity-50">
                    No, please do not contact me
                  </button>
                </div>
                <p className="mt-4 text-xs text-white/50">If you close this page without choosing, we won&rsquo;t contact you again.</p>
              </div>
            ) : (
              <div className="mt-8 flex items-start gap-3 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-5" data-testid="unsubscribe-done">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-300" />
                <p className="text-sm leading-relaxed text-white/85">
                  {choice
                    ? "Got it — we'll only get in touch when a funder is interested in businesses like yours. No other emails."
                    : "Understood — we won't contact you again. You can always sign back in to Clinq to update this."}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
